import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationIdInput,
  type ScopedAdminQueryKey,
} from '@/lib/organization-scoping'
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  createApiToken,
  getApiToken,
  regenerateApiToken,
  revokeApiToken,
  validateApiToken,
} from './api-tokens.api'
import type { ApiTokenValidationInput } from './api-tokens.schema'
import type { ApiTokenState } from './api-tokens.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

export const apiTokenQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'api-token' }, { organizationId }),
  ],
  show: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(apiTokenQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
    }),
}

export const apiTokenQueries = {
  show: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: apiTokenQueryKeys.show({ organizationId }),
      queryFn: getApiToken,
      initialData: () =>
        queryClient.getQueryData<ApiTokenState>(apiTokenQueryKeys.show({ organizationId })),
      initialDataUpdatedAt: () =>
        queryClient.getQueryState(apiTokenQueryKeys.show({ organizationId }))?.dataUpdatedAt,
    }),
}

export function useApiToken({
  organizationId,
  ...params
}: OrganizationScope &
  Partial<
    Omit<
      UseSuspenseQueryOptions<ApiTokenState, Error, ApiTokenState, ScopedAdminQueryKey>,
      'queryKey' | 'queryFn'
    >
  > = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...apiTokenQueries.show({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useCreateApiTokenMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => createApiToken({ organizationId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: apiTokenQueryKeys.all({ organizationId }),
      }),
  })
}

export function useRegenerateApiTokenMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => regenerateApiToken({ organizationId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: apiTokenQueryKeys.all({ organizationId }),
      }),
  })
}

export function useValidateApiTokenMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ token }: ApiTokenValidationInput) => validateApiToken({ organizationId, token }),
  })
}

export function useRevokeApiTokenMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => revokeApiToken({ organizationId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: apiTokenQueryKeys.all({ organizationId }),
      }),
  })
}
